import { logger } from '@lessonup/client-integration';
import { getShuffledSortingQuestionPinOrder } from '@lessonup/pins-shared';
import React from 'react';
import { usePinContext } from '../../../contexts/pinContext';
import { PollAnswerAreaContent } from './PollAnswerAreaContent/PollAnswerAreaContent';
import { SortingQuestionAnswerAreaContent } from './SortingQuestionAnswerAreaContent/SortingQuestionAnswerAreaContent';

export interface AnswerAreaContentProps {
  isInteractive?: boolean;
  selectedAnswerId?: string;
  onAnswerClick?: (answerId: string) => void;
}

/**
 * Switch to render the correct answer area content for the pin.
 * We re-use the AnswerAreaPinComponent for different types of pins,
 * but want to render different content for each type.
 *
 * @param editable - for instance shows/hides the placeholder cards for the open question pin
 * @param isPinComponentActive
 * @param isInteractive - whether buttons should be interactive (false renders as div to avoid nested buttons)
 * @param selectedAnswerId - optional: the currently selected answer ID (for interactive polls)
 * @param onAnswerClick - optional: callback when an answer is clicked (for interactive polls)
 * @returns the answer area content
 */
export function AnswerAreaContentSwitch({
  isInteractive = false,
  selectedAnswerId,
  onAnswerClick,
}: AnswerAreaContentProps) {
  const pin = usePinContext();

  switch (pin.type) {
    case 'POLL':
      return (
        <PollAnswerAreaContent
          pinSettings={pin.settings}
          isInteractive={isInteractive}
          selectedAnswerId={selectedAnswerId}
          onAnswerClick={onAnswerClick}
        />
      );
    case 'OPEN_QUESTION':
      return null;
    case 'SORTING_QUESTION': {
      const shuffledOrder = getShuffledSortingQuestionPinOrder(pin);
      return <SortingQuestionAnswerAreaContent answers={pin.settings.answers} order={shuffledOrder} />;
    }
    default:
      logger.error('Unhandled type in PinComponentSwitchDefaultRenderer', {
        pinId: pin.id,
        pinType: pin.type,
      });
      return null;
  }
}
